import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "neutral" | "success" | "danger";
  /** Si se setea, muestra un input opcional. La promesa resuelve con el string ingresado (o "" si vacío). Si el user cancela, resuelve `null`. */
  promptLabel?: string;
}

type Resolver = (value: string | boolean | null) => void;

interface ConfirmContextValue {
  open: (opts: ConfirmOptions) => Promise<string | boolean | null>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

// Registry global para llamadas fuera de React (`confirmAction(...)`).
let externalOpener: ConfirmContextValue["open"] | null = null;

export function confirmAction(opts: ConfirmOptions): Promise<string | boolean | null> {
  if (!externalOpener) {
    return Promise.resolve(window.confirm(opts.message ?? opts.title));
  }
  return externalOpener(opts);
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOptions; resolver: Resolver; value: string } | null>(null);

  const open = useCallback<ConfirmContextValue["open"]>((opts) => {
    return new Promise((resolve) => {
      setState({ opts, resolver: resolve, value: "" });
    });
  }, []);

  // Sincronizar opener externo
  useEffect(() => {
    externalOpener = open;
    return () => {
      externalOpener = null;
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!state) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        state.resolver(null);
        setState(null);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [state]);

  const handleConfirm = () => {
    if (!state) return;
    if (state.opts.promptLabel !== undefined) {
      state.resolver(state.value);
    } else {
      state.resolver(true);
    }
    setState(null);
  };

  const handleCancel = () => {
    if (!state) return;
    state.resolver(state.opts.promptLabel !== undefined ? null : false);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={{ open }}>
      {children}
      {state && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[90] animate-fade-in" onClick={handleCancel} aria-hidden />
          <div className="fixed inset-0 z-[91] flex items-center justify-center p-4 pointer-events-none">
            <div className="nx-card pointer-events-auto w-full max-w-md p-5 animate-fade-in" role="dialog" aria-modal="true">
              <h3 className="nx-h2 mb-2">{state.opts.title}</h3>
              {state.opts.message && (
                <p className="text-sm text-fg-muted mb-3">{state.opts.message}</p>
              )}
              {state.opts.promptLabel !== undefined && (
                <div className="mb-3">
                  <label className="nx-eyebrow block mb-1">{state.opts.promptLabel}</label>
                  <textarea
                    autoFocus
                    rows={3}
                    value={state.value}
                    onChange={(e) => setState({ ...state, value: e.target.value })}
                    className="nx-input resize-y"
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={handleCancel} className="nx-btn-secondary">
                  {state.opts.cancelLabel ?? "Cancelar"}
                </button>
                <button
                  onClick={handleConfirm}
                  className={cn(
                    state.opts.tone === "danger" && "nx-btn-danger",
                    state.opts.tone === "success" && "nx-btn-primary",
                    !state.opts.tone && "nx-btn-primary",
                  )}
                >
                  {state.opts.confirmLabel ?? "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
